import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";

// Standalone RM search-and-select (Feb 2026 — extracted from
// BillLineItemsTable's inline datalist pattern so callers outside
// the bill-line-items table can reuse the same "type-to-filter →
// pick from the shortlist" UX with a single-value selection API.
//
// Props:
//   value           — currently-selected rm_id (or "")
//   onChange(rmId)  — fires with the chosen rm_id (or "" on clear)
//   items           — array of {rm_id, description?, unit?, hsn?, ...}
//   placeholder     — input placeholder
//   emptyHint       — helper text when the shortlist is empty
//   disabled        — disables editing
//   testIdPrefix    — data-testid prefix (e.g. "jw-rm")
//   className       — outer wrapper className passthrough
export const RMSearchSelect = ({
  value = "",
  onChange,
  items = [],
  placeholder = "Type item ID or name…",
  emptyHint = "No matches",
  disabled = false,
  testIdPrefix = "rm-search",
  className = "",
}) => {
  const [search, setSearch] = useState(value || "");
  const [confirmed, setConfirmed] = useState(!!value);

  // Sync external value → internal state (e.g. form reset).
  useEffect(() => {
    setSearch(value || "");
    setConfirmed(!!value);
  }, [value]);

  const selectedItem = useMemo(
    () => (value ? items.find((it) => it.rm_id === value) : null),
    [value, items],
  );

  const matches = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items.slice(0, 50);
    return items.filter((it) =>
      it.rm_id.toLowerCase().includes(q) ||
      (it.description || "").toLowerCase().includes(q),
    ).slice(0, 50);
  }, [search, items]);

  const handleSelect = (rmId) => {
    if (!rmId) return;
    setSearch(rmId);
    setConfirmed(true);
    onChange && onChange(rmId);
  };

  const handleTyping = (v) => {
    setSearch(v);
    // Feb 2026: auto-confirm when the typed/pasted text exactly matches a
    // known rm_id (case-insensitive). Covers three UX paths that previously
    // left the parent's rm_id empty and blocked Preview/Commit:
    //   1. User picks from the browser-native <datalist> popup (fires as
    //      plain typing with the full rm_id as the new value).
    //   2. User pastes a valid rm_id.
    //   3. User types the full rm_id verbatim.
    const trimmed = (v || "").trim();
    if (trimmed) {
      const exact = items.find(
        (it) => (it.rm_id || "").toLowerCase() === trimmed.toLowerCase(),
      );
      if (exact) {
        setConfirmed(true);
        onChange && onChange(exact.rm_id);
        return;
      }
    }
    if (confirmed) {
      setConfirmed(false);
      onChange && onChange("");
    }
  };

  const listId = `${testIdPrefix}-datalist`;

  return (
    <div className={className}>
      <Input
        value={search}
        onChange={(e) => handleTyping(e.target.value)}
        placeholder={placeholder}
        className="font-mono text-xs h-9"
        list={listId}
        disabled={disabled}
        data-testid={`${testIdPrefix}-input`}
      />
      <datalist id={listId}>
        {matches.map((rm) => (
          <option key={rm.rm_id} value={rm.rm_id}>
            {rm.description || rm.name || ""}
          </option>
        ))}
      </datalist>
      {search && !confirmed && (
        <select
          className="w-full text-xs border rounded px-1 py-1 bg-gray-50 mt-1"
          value=""
          onChange={(e) => handleSelect(e.target.value)}
          disabled={disabled}
          data-testid={`${testIdPrefix}-picker`}
        >
          <option value="">
            {matches.length === 0 ? emptyHint : "Select from matches"}
          </option>
          {matches.map((rm) => (
            <option key={rm.rm_id} value={rm.rm_id}>
              {rm.rm_id}{rm.description ? ` — ${rm.description}` : ""}
            </option>
          ))}
        </select>
      )}
      {confirmed && selectedItem && (
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-[11px] text-zinc-600">
          <span className="bg-blue-100 text-blue-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
            Selected
          </span>
          <span className="font-mono">{selectedItem.rm_id}</span>
          {selectedItem.description && (
            <span className="text-zinc-500 truncate">— {selectedItem.description}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default RMSearchSelect;
